import React from "react";
import styled from "styled-components";
import { Filters } from "@sonder/ui/source/icons";
import { Collapser } from "@sonder/ui/source/styled/components/Collapser";
import { Switch } from "@sonder/ui/source/styled/components/Switch";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { Button as ButtonPrimitive } from "@sonder/ui/source/styled/primitives/Button";
import { borders, colors } from "@sonder/ui/source/styled/theme";
import { TaskContextSubjectTypeEnum } from "typings/globalGQLTypes";

const AdditionalFiltersButton = styled(ButtonPrimitive)<{ isOpen: boolean }>`
  color: ${props => (props.isOpen ? colors.blue7 : "inherit")};
  padding-right: 0.5rem;
`;

const AdditionalFiltersInputField = styled(InputField)`
  align-items: center;
  flex-direction: row;

  label:first-child {
    margin: 0;
  }

  label:last-child {
    margin: 0 0 0 0.5rem;
  }
`;

const AdditionalFiltersInputGroup = styled(InputGroup)`
  border-bottom: ${borders.standard};
  display: flex;
  flex: 1 1 auto;
  padding: 0.5rem;

  ${InputField} {
    margin: 0;
  }
`;

export interface AdditionalFiltersState {
  unit: {
    live: boolean;
  };
}

export interface AdditionalFiltersTriggerProps {
  isOpen: boolean;
  onClick: () => void;
}

export interface AdditionalFiltersProps {
  additionalFilters: AdditionalFiltersState;
  subjectType: TaskContextSubjectTypeEnum;
  onChange: (filters: AdditionalFiltersState) => void;
}

export interface AdditionalFiltersContainerProps
  extends AdditionalFiltersProps {
  isOpen: boolean;
}

export const AdditionalFiltersTrigger = ({
  isOpen,
  onClick,
}: AdditionalFiltersTriggerProps) => (
  <AdditionalFiltersButton
    isOpen={isOpen}
    title={`${isOpen ? "Hide additional filters" : "Show additional filters"}`}
    onClick={onClick}
  >
    <Filters />
  </AdditionalFiltersButton>
);

const AdditionalUnitFilters = ({
  additionalFilters,
  onChange,
}: AdditionalFiltersProps) => {
  const { unit } = additionalFilters;
  return (
    <>
      <AdditionalFiltersInputField>
        <Label>Live</Label>
        <Switch
          checked={unit.live}
          title="Live"
          onChange={() => {
            onChange({
              ...additionalFilters,
              unit: {
                live: !unit.live,
              },
            });
          }}
        />
      </AdditionalFiltersInputField>
    </>
  );
};

const AdditionalSubjectFilters = ({
  additionalFilters,
  onChange,
  subjectType,
}: AdditionalFiltersProps) => {
  switch (subjectType) {
    case TaskContextSubjectTypeEnum.Unit:
      return (
        <AdditionalUnitFilters
          additionalFilters={additionalFilters}
          subjectType={subjectType}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
};

export const AdditionalFilters = ({
  additionalFilters,
  isOpen,
  onChange,
  subjectType,
}: AdditionalFiltersContainerProps) => {
  return (
    <Collapser isCollapsed={!isOpen}>
      <AdditionalFiltersInputGroup>
        <AdditionalSubjectFilters
          additionalFilters={additionalFilters}
          subjectType={subjectType}
          onChange={onChange}
        />
      </AdditionalFiltersInputGroup>
    </Collapser>
  );
};
